import type { PhysicalAddress } from '@/features/tax-profile/PhysicalAddress'
import { action, observable } from 'mobx'
import { required, validationContext } from 'validx'
import { US_STATES_AND_TERRITORIES } from '@/assets/data/us-states-territories'
import { COUNTRIES, COUNTRY_US } from '@/assets/data/countries'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  when,
} from '@/utils/validx-util'

/**
 * View model for editing a physical address.
 */
export class PhysicalAddressEditorViewModel {
  @observable accessor addressLine1 = ''
  @observable accessor addressLine2 = ''
  @observable accessor city = ''
  @observable accessor region = ''
  @observable accessor postalCode = ''
  @observable accessor country = ''

  readonly validation = validationContext(this, {
    addressLine1: [
      required('Address line 1 is required'),
      mustHaveMaxLength(100, 'Address line 1 must not exceed 100 characters'),
      mustBeValidTaxFormInputValue(),
    ],
    addressLine2: [
      mustHaveMaxLength(100, 'Address line 2 must not exceed 100 characters'),
      mustBeValidTaxFormInputValue(),
    ],
    city: [
      required('City is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'City must not exceed 100 characters'),
    ],
    region: [
      when(
        // Require the region (state) when the country is U.S.
        () => this.country === COUNTRY_US[1],
        required('State or territory is required'),
      ),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Region must not exceed 100 characters'),
    ],
    postalCode: [
      required('Zip/postal code is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(20, 'Zip/postal code must not exceed 20 characters'),
    ],
    country: [required('Country is required')],
  })

  /**
   * Resets the state of the view model to that of the given
   * info, or defaults if none is specified.
   *
   * @param info
   */
  @action.bound
  reset(info?: PhysicalAddress) {
    this.addressLine1 = info?.addressLine1 ?? ''
    this.addressLine2 = info?.addressLine2 ?? ''
    this.city = info?.city ?? ''
    this.postalCode = info?.postalCode ?? ''
    this.region = info?.region ? tryNormalizeState(info.region) : ''
    this.country = info?.country
      ? tryNormalizeCountry(info.country)
      : // Default to U.S.
        COUNTRY_US[1]

    this.validation.reset()
  }

  /**
   * Maps the state of the VM to {@link PhysicalAddress}.
   */
  toPhysicalAddress(): PhysicalAddress {
    return {
      addressLine1: this.addressLine1,
      addressLine2: this.addressLine2,
      city: this.city,
      region: this.region,
      postalCode: this.postalCode,
      country: this.country,
    }
  }
}

/**
 * Tries to normalize the U.S. state or territory value.
 * It will also normalize the casing to that of the known label.
 *
 * @param input
 * @returns
 */
function tryNormalizeState(input: string) {
  const lower = input.toLowerCase()
  const found = US_STATES_AND_TERRITORIES.find(([code, label]) => {
    return code.toLowerCase() === lower || label.toLowerCase() === lower
  })
  if (found) {
    return found[1]
  }

  return input
}

/**
 * Tries to normalize the country value.
 * If the input is a code, it will return the label.
 * It will also normalize the casing to that of the known label.
 *
 * @param input
 * @returns
 */
function tryNormalizeCountry(input: string) {
  const lower = input.toLowerCase()
  const found = COUNTRIES.find(([code, label]) => {
    return code.toLowerCase() === lower || label.toLowerCase() === lower
  })
  if (found) {
    return found[1]
  }

  return input
}
